import React from "react";
import { PageHeader } from "@/components/Layout";
import { useSettings } from "@/lib/useSettings";
import { Copy, IndianRupee, QrCode, Landmark } from "lucide-react";
import { toast } from "sonner";

function Field({ label, value }) {
    const copy = () => {
        navigator.clipboard.writeText(value);
        toast.success(`${label} copied`);
    };
    return (
        <div className="flex items-center justify-between gap-3 py-3 border-b border-border last:border-0">
            <div>
                <div className="font-eyebrow text-[10px] text-muted-foreground">{label}</div>
                <div className="font-medium">{value}</div>
            </div>
            <button onClick={copy} data-testid={`copy-${label.toLowerCase().replace(/\s+/g, "-")}`} className="h-8 w-8 grid place-items-center hover:bg-accent transition-colors">
                <Copy className="h-4 w-4" />
            </button>
        </div>
    );
}

export default function Donate() {
    const { data: settings } = useSettings();
    const s = settings || {};

    return (
        <div data-testid="donate-page">
            <PageHeader
                eyebrow="Donate"
                title={<>Every rupee plants a <span className="text-secondary italic font-light">small forest of hope.</span></>}
                sub="100% of your donation reaches our programs. We publish quarterly impact updates to subscribers and donors."
            />

            <section className="mx-auto max-w-6xl px-5 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* UPI / QR */}
                <div className="lg:col-span-5 bg-card border border-border p-8">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="h-10 w-10 rounded-full bg-secondary/10 grid place-items-center">
                            <QrCode className="h-5 w-5 text-secondary" />
                        </div>
                        <h2 className="font-display text-2xl">Donate via UPI</h2>
                    </div>
                    {s.upi_qr_image ? (
                        <img src={s.upi_qr_image} alt="UPI QR" className="w-full max-w-xs mx-auto border border-border p-3 bg-background" />
                    ) : (
                        <div className="aspect-square max-w-xs mx-auto bg-muted/50 border border-dashed border-border grid place-items-center text-muted-foreground">
                            <div className="text-center px-6">
                                <QrCode className="h-16 w-16 mx-auto mb-3 opacity-40" />
                                <p className="text-xs">QR code will be available shortly. Use UPI ID below.</p>
                            </div>
                        </div>
                    )}
                    <div className="mt-6 bg-accent/40 p-4 text-center">
                        <div className="font-eyebrow text-[10px] text-muted-foreground mb-1">UPI ID</div>
                        <div className="font-display text-lg break-all" data-testid="upi-id-display">{s.upi_id}</div>
                    </div>
                </div>

                {/* Bank details */}
                <div className="lg:col-span-7 bg-card border border-border p-8">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="h-10 w-10 rounded-full bg-primary/10 grid place-items-center">
                            <Landmark className="h-5 w-5 text-primary" />
                        </div>
                        <h2 className="font-display text-2xl">Bank transfer</h2>
                    </div>
                    <div className="text-sm">
                        <Field label="Account name" value={s.account_name} />
                        <Field label="Bank" value={s.bank_name} />
                        <Field label="Account number" value={s.account_number} />
                        <Field label="IFSC" value={s.ifsc} />
                        <Field label="Branch" value={s.branch} />
                    </div>
                    <div className="mt-6 bg-secondary/10 border-l-4 border-secondary p-4 text-sm leading-relaxed text-foreground/85">
                        <div className="flex items-start gap-2">
                            <IndianRupee className="h-4 w-4 mt-0.5 text-secondary shrink-0" />
                            <span>{s.donation_note}</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-4xl px-5 lg:px-10 pb-20">
                <div className="bg-primary text-primary-foreground p-10 lg:p-12 text-center">
                    <div className="font-eyebrow text-xs text-secondary mb-3">After you donate</div>
                    <h3 className="font-display text-2xl lg:text-3xl leading-tight">
                        Send your transaction reference to{" "}
                        <a href={`mailto:${s.contact_email}`} className="underline underline-offset-4">{s.contact_email}</a>{" "}
                        to receive your receipt.
                    </h3>
                </div>
            </section>
        </div>
    );
}
